import { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { candidatesApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Upload, FileText, X, Loader2, CheckCircle, AlertCircle,
  Phone, Mail, User, MessageSquare, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const CHUNK_SIZE = 5; // Upload 5 files at a time

export default function BulkUploadDialog({ open, onOpenChange, onComplete }) {
  const [files, setFiles] = useState([]);
  const [aiProvider, setAiProvider] = useState('anthropic');
  const [sendWhatsapp, setSendWhatsapp] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState(''); // 'uploading' | 'processing'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const pollRef = useRef(null);

  const onDrop = useCallback((accepted) => {
    setFiles(prev => [...prev, ...accepted].slice(0, 100));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf'],
    },
    maxFiles: 100,
    multiple: true,
    disabled: uploading || !!batchId,
  });

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // Poll batch status
  useEffect(() => {
    if (!batchId || uploadPhase !== 'processing') return;
    
    const poll = async () => {
      try {
        const res = await candidatesApi.getBulkUploadStatus(batchId);
        setBatchStatus(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch { /* ignore */ }
    };
    
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [batchId, uploadPhase]);

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadPhase('uploading');
    setUploadProgress(0);
    
    try {
      // Step 1: Create batch
      const createRes = await candidatesApi.createBulkBatch(aiProvider, sendWhatsapp, files.length);
      const newBatchId = createRes.data.batch_id;
      setBatchId(newBatchId);
      
      // Step 2: Upload in chunks of CHUNK_SIZE
      const totalChunks = Math.ceil(files.length / CHUNK_SIZE);
      let totalDupes = 0;
      
      for (let i = 0; i < totalChunks; i++) {
        const chunk = files.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        try {
          const appendRes = await candidatesApi.appendToBulkBatch(newBatchId, chunk);
          totalDupes += appendRes.data.duplicates_skipped || 0;
        } catch (err) {
          console.error(`Chunk ${i + 1} failed:`, err);
          // Continue with remaining chunks
        }
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
      
      // Step 3: Start processing
      setUploadPhase('processing');
      await candidatesApi.startBulkBatch(newBatchId);
      
      const msg = totalDupes > 0
        ? `${files.length - totalDupes} CVs uploaded, ${totalDupes} duplicates skipped. Processing...`
        : `${files.length} CVs uploaded! Processing one by one...`;
      toast.success(msg);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please try again.');
      setUploading(false);
      setUploadPhase('');
      setBatchId(null);
    }
  };

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (batchStatus?.status === 'completed' || batchStatus?.status === 'failed') {
      onComplete?.(batchStatus?.completed || 0, batchStatus?.failed || 0);
    }
    setFiles([]);
    setBatchId(null);
    setBatchStatus(null);
    setUploading(false);
    setUploadPhase('');
    setUploadProgress(0);
    onOpenChange(false);
  };

  const isProcessing = uploadPhase === 'processing' && batchStatus?.status === 'processing';
  const isDone = batchStatus?.status === 'completed' || batchStatus?.status === 'failed';
  const totalProcessable = batchStatus ? batchStatus.items.filter(i => i.status !== 'duplicate').length : 0;
  const progress = batchStatus ? ((batchStatus.completed + batchStatus.failed) / Math.max(totalProcessable, 1)) * 100 : 0;
  const dupeCount = batchStatus?.duplicates || batchStatus?.items?.filter(i => i.status === 'duplicate').length || 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-blue-600" />
            Bulk Upload CVs
            {batchStatus && (
              <Badge variant={isDone ? (batchStatus.failed === 0 ? 'default' : 'secondary') : 'outline'} className="ml-2">
                {batchStatus.completed}/{batchStatus.total} parsed
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Config section - only show before upload starts */}
        {!batchId && !uploading && (
          <div className="space-y-4">
            <div className="flex items-center gap-6">
              <div className="space-y-1.5 flex-1">
                <Label className="text-xs text-slate-500">AI Provider</Label>
                <Select value={aiProvider} onValueChange={setAiProvider}>
                  <SelectTrigger data-testid="bulk-ai-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anthropic">Claude (Anthropic)</SelectItem>
                    <SelectItem value="openai">GPT (OpenAI)</SelectItem>
                    <SelectItem value="gemini">Gemini (Google)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-5">
                <Switch
                  id="send-wa"
                  checked={sendWhatsapp}
                  onCheckedChange={setSendWhatsapp}
                  data-testid="bulk-send-whatsapp"
                />
                <Label htmlFor="send-wa" className="flex items-center gap-1.5 cursor-pointer text-sm">
                  <MessageSquare className="w-4 h-4 text-green-600" />
                  Send WhatsApp after each parse
                </Label>
              </div>
            </div>

            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors",
                isDragActive ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300",
                files.length > 0 && "p-4"
              )}
              data-testid="bulk-dropzone"
            >
              <input {...getInputProps()} data-testid="bulk-file-input" />
              <Upload className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600">Drop up to 100 CVs here, or click to browse</p>
              <p className="text-xs text-slate-400 mt-1">PDF, DOCX, DOC, TXT, RTF</p>
            </div>

            {/* File list before upload */}
            {files.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">{files.length} file{files.length > 1 ? 's' : ''} selected</span>
                  <Button variant="ghost" size="sm" onClick={() => setFiles([])} className="text-red-500 text-xs h-7">
                    Clear all
                  </Button>
                </div>
                <ScrollArea className="max-h-40">
                  <div className="space-y-1">
                    {files.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded text-sm">
                        <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="flex-1 truncate text-slate-600">{file.name}</span>
                        <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)}KB</span>
                        <button onClick={() => removeFile(idx)} className="p-0.5 text-slate-400 hover:text-red-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            <Button
              onClick={handleUpload}
              disabled={files.length === 0}
              className="w-full"
              data-testid="bulk-upload-submit"
            >
              <Upload className="w-4 h-4 mr-2" /> Upload & Process {files.length} CV{files.length !== 1 ? 's' : ''}
            </Button>
          </div>
        )}

        {/* Upload progress (chunked upload phase) */}
        {uploadPhase === 'uploading' && (
          <div className="space-y-3 py-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              <span className="text-sm text-slate-600">Uploading files in batches of {CHUNK_SIZE}...</span>
              <span className="ml-auto text-sm font-medium">{uploadProgress}%</span>
            </div>
            <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-blue-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className="text-xs text-slate-400">Please wait, this may take a moment for large batches.</p>
          </div>
        )}

        {/* Processing view */}
        {uploadPhase === 'processing' && batchStatus && (
          <div className="space-y-4 flex-1 min-h-0">
            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {isProcessing ? 'Processing one by one...' : isDone ? 'Processing complete' : 'Starting...'}
                </span>
                <span className="font-medium text-slate-800">
                  {batchStatus.completed + batchStatus.failed}/{totalProcessable}
                </span>
              </div>
              <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    isDone ? (batchStatus.failed === 0 ? "bg-green-500" : "bg-amber-500") : "bg-blue-500"
                  )}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-green-500" /> {batchStatus.completed} parsed
                </span>
                {batchStatus.failed > 0 && (
                  <span className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-red-500" /> {batchStatus.failed} failed
                  </span>
                )}
                {dupeCount > 0 && (
                  <span className="flex items-center gap-1">
                    <FileText className="w-3 h-3 text-amber-500" /> {dupeCount} duplicates skipped
                  </span>
                )}
                {sendWhatsapp && (
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3 h-3 text-green-600" /> {batchStatus.whatsapp_sent} WhatsApp sent
                  </span>
                )}
              </div>
            </div>

            {/* Items list */}
            <ScrollArea className="flex-1 max-h-[50vh]">
              <div className="space-y-1.5 pr-3">
                {batchStatus.items.map((item) => (
                  <div
                    key={item.id}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border text-sm",
                      item.status === 'completed' ? "bg-green-50/50 border-green-100" :
                      item.status === 'parsing' ? "bg-blue-50/50 border-blue-100" :
                      item.status === 'failed' ? "bg-red-50/50 border-red-100" :
                      item.status === 'duplicate' ? "bg-amber-50/50 border-amber-100" :
                      "bg-white border-slate-100"
                    )}
                    data-testid={`bulk-item-${item.id}`}
                  >
                    <div className="flex-shrink-0">
                      {item.status === 'completed' && <CheckCircle className="w-5 h-5 text-green-500" />}
                      {item.status === 'parsing' && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />}
                      {item.status === 'failed' && <AlertCircle className="w-5 h-5 text-red-500" />}
                      {item.status === 'duplicate' && <FileText className="w-5 h-5 text-amber-500" />}
                      {item.status === 'pending' && <FileText className="w-5 h-5 text-slate-300" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      {item.status === 'completed' ? (
                        <div>
                          <p className="font-medium text-slate-800 truncate">
                            <User className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                            {item.candidate_name || 'Unknown'}
                          </p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-slate-500">
                            {item.candidate_email && (
                              <span className="flex items-center gap-0.5">
                                <Mail className="w-3 h-3" /> {item.candidate_email}
                              </span>
                            )}
                            {item.candidate_phone && (
                              <span className="flex items-center gap-0.5">
                                <Phone className="w-3 h-3" /> {item.candidate_phone}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : item.status === 'duplicate' ? (
                        <div>
                          <p className="text-amber-700 truncate font-medium">Duplicate - {item.original_filename}</p>
                          <p className="text-xs text-amber-600 mt-0.5">Already uploaded{item.candidate_name ? ` for ${item.candidate_name}` : ''}. Skipped to save API cost.</p>
                        </div>
                      ) : (
                        <p className="text-slate-500 truncate">{item.original_filename}</p>
                      )}
                      {item.status === 'failed' && item.error && (
                        <p className="text-xs text-red-500 mt-0.5 truncate">{item.error}</p>
                      )}
                    </div>

                    {item.status === 'completed' && item.whatsapp_status && (
                      <Badge
                        variant={item.whatsapp_status === 'sent' ? 'default' : item.whatsapp_status === 'skipped' ? 'secondary' : 'destructive'}
                        className="text-xs flex-shrink-0"
                      >
                        <MessageSquare className="w-3 h-3 mr-1" />
                        {item.whatsapp_status === 'sent' ? 'WA Sent' : item.whatsapp_status === 'skipped' ? 'No Phone' : 'WA Failed'}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>

            {isDone && (
              <Button onClick={handleClose} className="w-full" data-testid="bulk-upload-done">
                <CheckCircle className="w-4 h-4 mr-2" />
                Done - {batchStatus.completed} Candidates Added
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
